import type { InfoClient } from "@nktkas/hyperliquid";
import { useQuery } from "@tanstack/react-query";
import { getInfoClient } from "#/features/trade/lib/hyperliquid";
import { toNumber } from "#/lib/formatters";
import {
	BUILDER_ADDRESS,
	BUILDER_ENABLED,
	BUILDER_FEE,
	BUILDER_FEE_RATE,
} from "./builder-fee";
import { queryKeys } from "./query-keys";
import { useWallet } from "./use-wallet";

type UserFees = Awaited<ReturnType<InfoClient["userFees"]>>;

const BUILDER_FEE_DECIMAL_RATE = BUILDER_FEE / 100_000;

function applyReferralDiscount(rate: number, referralDiscount: number) {
	if (rate <= 0 || referralDiscount <= 0) return rate;
	return rate * (1 - referralDiscount);
}

export type FeeTierRow = {
	tier: number;
	notionalCutoff: number;
	takerRate: number;
	makerRate: number;
	spotTakerRate: number;
	spotMakerRate: number;
};

export type MakerRebateRow = {
	tier: number;
	makerShareCutoff: number;
	makerRate: number;
};

function toFeeTierRows(data: UserFees | undefined): FeeTierRow[] {
	if (!data) return [];
	const { feeSchedule } = data;
	const regularRow: FeeTierRow = {
		tier: 0,
		notionalCutoff: 0,
		takerRate: toNumber(feeSchedule.cross),
		makerRate: toNumber(feeSchedule.add),
		spotTakerRate: toNumber(feeSchedule.spotCross),
		spotMakerRate: toNumber(feeSchedule.spotAdd),
	};
	const vipRows = feeSchedule.tiers.vip.map((tier, index) => ({
		tier: index + 1,
		notionalCutoff: toNumber(tier.ntlCutoff),
		takerRate: toNumber(tier.cross),
		makerRate: toNumber(tier.add),
		spotTakerRate: toNumber(tier.spotCross),
		spotMakerRate: toNumber(tier.spotAdd),
	}));
	return [regularRow, ...vipRows];
}

function toMakerRebateRows(data: UserFees | undefined): MakerRebateRow[] {
	return (
		data?.feeSchedule.tiers.mm.map((tier, index) => ({
			tier: index + 1,
			makerShareCutoff: toNumber(tier.makerFractionCutoff),
			makerRate: toNumber(tier.add),
		})) ?? []
	);
}

export function useHyperFees() {
	const { address } = useWallet();
	const infoClient = getInfoClient();

	const userFeesQuery = useQuery({
		queryKey: queryKeys.hyper.userFees(address),
		queryFn: () =>
			infoClient.userFees({
				user: address as `0x${string}`,
			}),
		staleTime: 20_000,
		refetchInterval: 30_000,
		enabled: address !== null,
	});

	const builderApprovalQuery = useQuery({
		queryKey: queryKeys.hyper.maxBuilderFee(address, BUILDER_ADDRESS),
		queryFn: () =>
			infoClient.maxBuilderFee({
				user: address as `0x${string}`,
				builder: BUILDER_ADDRESS,
			}),
		staleTime: 20_000,
		refetchInterval: 30_000,
		enabled: address !== null && BUILDER_ENABLED,
	});

	const fees = userFeesQuery.data;
	const maxBuilderFee = builderApprovalQuery.data ?? 0;
	const builderFeeActive = BUILDER_ENABLED && maxBuilderFee >= BUILDER_FEE;
	const reportedHyperliquidTakerRate = toNumber(fees?.userCrossRate);
	const reportedHyperliquidMakerRate = toNumber(fees?.userAddRate);
	const referralDiscountRate = toNumber(fees?.activeReferralDiscount);
	const stakingDiscountRate = toNumber(fees?.activeStakingDiscount.discount);
	const effectiveHyperliquidTakerRate = applyReferralDiscount(
		reportedHyperliquidTakerRate,
		referralDiscountRate,
	);
	const effectiveHyperliquidMakerRate = applyReferralDiscount(
		reportedHyperliquidMakerRate,
		referralDiscountRate,
	);
	// The builder fee is charged on every order whenever BUILDER_ENABLED — orders
	// carry `{ b, f: BUILDER_FEE }` and approval is auto-ensured at order time
	// (see use-trading-actions / ensureBuilderFeeApproved). So the all-in rate must
	// include it regardless of whether the user has approved on-chain yet
	// (builderFeeActive), otherwise a not-yet-approved (new) user sees an
	// understated fee that never matches what they actually pay.
	const builderRate = BUILDER_ENABLED ? BUILDER_FEE_DECIMAL_RATE : 0;
	const totalTakerRate = effectiveHyperliquidTakerRate + builderRate;
	const totalMakerRate = effectiveHyperliquidMakerRate + builderRate;

	return {
		fees,
		userFeesQuery,
		builderApprovalQuery,
		builderFeeActive,
		builderFeeRate: BUILDER_FEE_DECIMAL_RATE,
		builderFeeRateLabel: BUILDER_FEE_RATE,
		builderMaxApprovedRate: maxBuilderFee / 100_000,
		reportedHyperliquidTakerRate,
		reportedHyperliquidMakerRate,
		effectiveHyperliquidTakerRate,
		effectiveHyperliquidMakerRate,
		totalTakerRate,
		totalMakerRate,
		referralDiscountRate,
		stakingDiscountRate,
		hasReferralDiscount: referralDiscountRate > 0,
		hasStakingDiscount: stakingDiscountRate > 0,
		feeTierRows: toFeeTierRows(fees),
		makerRebateRows: toMakerRebateRows(fees),
	};
}
